import { oidcConfig } from 'common/api/oidc/config';
import { AuthLocation, getAuthDomain, getCurrentAuthLocation } from 'common/api/auth/authDomain';
import { IS_FLIPGIVE } from 'common/env';
import { fromByteArray } from 'base64-js';
import { sha256 } from 'js-sha256';
import { platformInfo } from 'common/utils/platformInfo';

export type SocialRegistrationProvider = 'google' | 'apple';

export interface ExternalSocialProvider {
    provider: SocialRegistrationProvider;
    scheme: string;
}

export type SocialRegistrationOutcome =
    | 'success'
    | 'existing_account'
    | 'missing_email'
    | 'invalid_key'
    | 'expired_key'
    | 'used_key';

export interface SocialRegistrationResumeParams {
    outcome?: string;
    provider?: string;
    externalRegistrationKey?: string;
}

export interface SocialRegistrationChallenge {
    codeVerifier: string;
    codeChallenge: string;
    codeChallengeMethod: 'S256';
}

export interface ExternalRegistrationPeekResponse {
    outcome: SocialRegistrationOutcome;
    provider?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    emailRequired?: boolean;
}

interface ExternalProviderResponse {
    scheme?: unknown;
}

const socialRegistrationSchemes: Record<SocialRegistrationProvider, string> = {
    google: IS_FLIPGIVE ? 'Google-FlipGive' : 'Google-RaiseRight',
    apple: IS_FLIPGIVE ? 'Apple-FlipGive' : 'Apple-RaiseRight',
};

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const isFlipGiveIssuer = (issuer: string) => {
    try {
        return new URL(issuer).hostname.toLowerCase().includes('flipgive');
    } catch (_error) {
        return false;
    }
};

export const getDefaultExternalSocialProviders = (issuer?: string): ExternalSocialProvider[] => {
    let shouldFailOpen = IS_FLIPGIVE;
    if (issuer) {
        shouldFailOpen = isFlipGiveIssuer(issuer);
    }

    if (!shouldFailOpen) {
        return [];
    }

    return [
        { provider: 'google', scheme: 'Google-FlipGive' },
        { provider: 'apple', scheme: 'Apple-FlipGive' },
    ];
};

export const getSocialRegistrationIssuer = (
    issuerUrl: string = process.env.REACT_APP_OIDC_ISSUER_URL || '',
    location: AuthLocation = getCurrentAuthLocation()
) => {
    if (!issuerUrl) {
        throw new Error(
            'Missing required social registration auth configuration: REACT_APP_OIDC_ISSUER_URL'
        );
    }

    return new URL(issuerUrl.replace('domain', getAuthDomain(location))).origin;
};

const base64UrlEncode = (bytes: number[] | Uint8Array) =>
    fromByteArray(Uint8Array.from(bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

export const SOCIAL_REGISTRATION_CALLBACK_ROUTE = 'mobile/enrollment/social-registration';
export const SOCIAL_REGISTRATION_CALLBACK_PATH = `/${SOCIAL_REGISTRATION_CALLBACK_ROUTE}`;

/** Label for account settings / toasts (e.g. Google, Apple). */
export const getSocialProviderDisplayName = (provider?: string | null): string => {
    const normalized = normalizeSocialRegistrationProvider(provider ?? undefined);
    if (normalized === 'google') {
        return 'Google';
    }
    if (normalized === 'apple') {
        return 'Apple';
    }
    return 'Social';
};

export const normalizeSocialRegistrationProvider = (
    provider?: string
): SocialRegistrationProvider | undefined => {
    const normalizedProvider = provider?.trim().toLowerCase();

    if (!normalizedProvider) {
        return undefined;
    }

    if (normalizedProvider.includes('google')) {
        return 'google';
    }

    if (normalizedProvider.includes('apple')) {
        return 'apple';
    }

    return undefined;
};

export const normalizeExternalSocialProviders = (
    providers?: ExternalProviderResponse[] | null
): ExternalSocialProvider[] => {
    if (!Array.isArray(providers)) {
        return [];
    }

    return providers.reduce<ExternalSocialProvider[]>((availableProviders, providerResponse) => {
        const scheme =
            typeof providerResponse?.scheme === 'string' ? providerResponse.scheme.trim() : '';
        const provider = normalizeSocialRegistrationProvider(scheme);
        if (!scheme || !provider || availableProviders.some((p) => p.provider === provider)) {
            return availableProviders;
        }

        availableProviders.push({ provider, scheme });
        return availableProviders;
    }, []);
};

export const buildSocialRegistrationReturnUrl = (appUrlScheme?: string) =>
    appUrlScheme
        ? `${appUrlScheme}://${SOCIAL_REGISTRATION_CALLBACK_ROUTE}`
        : `${trimTrailingSlash(oidcConfig.issuer)}${SOCIAL_REGISTRATION_CALLBACK_PATH}`;

export const createSocialRegistrationChallenge = (): SocialRegistrationChallenge => {
    const crypto = globalThis.crypto;
    if (!crypto?.getRandomValues) {
        throw new Error('Missing required crypto.getRandomValues for social registration.');
    }

    const verifierBytes = new Uint8Array(32);
    crypto.getRandomValues(verifierBytes);
    const codeVerifier = base64UrlEncode(verifierBytes);
    const codeChallenge = base64UrlEncode(sha256.array(codeVerifier));

    return {
        codeVerifier,
        codeChallenge,
        codeChallengeMethod: 'S256',
    };
};

export const parseSocialRegistrationReturnUrl = (url: string): SocialRegistrationResumeParams => {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) {
        return {};
    }

    const params: SocialRegistrationResumeParams = {};
    const pairs = url.slice(queryIndex + 1).split('&');
    for (const pair of pairs) {
        if (!pair) {
            continue;
        }
        const [rawKey, rawValue = ''] = pair.split('=');
        const key = decodeURIComponent(rawKey);
        const value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
        if (key === 'outcome') {
            params.outcome = value;
        } else if (key === 'provider') {
            params.provider = value;
        } else if (key === 'externalRegistrationKey') {
            params.externalRegistrationKey = value;
        }
    }
    return params;
};

export const buildSocialRegistrationChallengeUrl = (
    provider: SocialRegistrationProvider,
    returnUrl: string,
    issuer: string = oidcConfig.issuer,
    challenge?: Pick<SocialRegistrationChallenge, 'codeChallenge' | 'codeChallengeMethod'>,
    providerScheme: string = socialRegistrationSchemes[provider]
) => {
    const authBaseUrl = trimTrailingSlash(issuer);
    const params = new URLSearchParams({
        scheme: providerScheme,
        returnUrl,
        ...(challenge
            ? {
                  codeChallenge: challenge.codeChallenge,
                  codeChallengeMethod: challenge.codeChallengeMethod,
              }
            : {}),
    });

    return `${authBaseUrl}/External/RegistrationChallenge?${params.toString()}`;
};

export const getExternalSocialProviders = async (
    issuer: string = oidcConfig.issuer
): Promise<ExternalSocialProvider[]> => {
    const authBaseUrl = trimTrailingSlash(issuer);
    try {
        const url =
            platformInfo.platform === 'web'
                ? '/api/local/social-registration/providers'
                : `${authBaseUrl}/External/Providers`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                ...(platformInfo.platform === 'web' ? { 'X-CSRF': '1' } : {}),
            },
        });

        if (!response.ok) {
            return getDefaultExternalSocialProviders(authBaseUrl);
        }

        return normalizeExternalSocialProviders(await response.json());
    } catch (_error) {
        return getDefaultExternalSocialProviders(authBaseUrl);
    }
};

export const peekExternalRegistrationSession = async (
    externalRegistrationKey: string,
    codeVerifier: string,
    issuer: string = oidcConfig.issuer
): Promise<ExternalRegistrationPeekResponse> => {
    const authBaseUrl = trimTrailingSlash(issuer);
    const response = await fetch(`${authBaseUrl}/External/PeekRegistrationSession`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            externalRegistrationKey,
            codeVerifier,
        }),
    });

    if (!response.ok) {
        throw new Error('Unable to peek external registration session.');
    }

    return response.json();
};
